import { useCallback } from 'react'

import { NotificationsSubscriptionDocument } from 'generated/resources/graphql'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { Notification } from 'modules/job_catalog/hooks/useNotifications'

/**
 * React to a specific notifier event received via WebSocket.
 *
 * Listens to the global `notificationsInboxNotificationCreate` subscription and
 * fires the callback only when the notification's `notifier` field matches.
 *
 * The `notifier` field corresponds to the notifier class name in the backend,
 * e.g. `"Trainings::FreeCourseReadyNotifier"`.
 *
 * @param notifier - A notifier class name or array of notifier class names to listen for.
 * @param callback - Called with the notification payload when a matching notification arrives.
 *
 * @example
 * // Single notifier — refetch a query when a course is ready
 * useNotifierEvent('Trainings::FreeCourseReadyNotifier', useCallback(() => {
 *   apolloClient.refetchQueries({ include: [FreeCoursesDataCollectionDocument] })
 * }, [apolloClient]))
 *
 * @example
 * // Multiple notifiers — react to any of them
 * useNotifierEvent(
 *   ['Trainings::FreeCourseReadyNotifier', 'Trainings::FreeCoursePublishNotifier'],
 *   useCallback((notification) => {
 *     console.log('Received:', notification.title)
 *   }, [])
 * )
 */
export const useNotifierEvent = (
  notifier: string | string[],
  callback: (notification: Notification) => void
) => {
  const notifiers = Array.isArray(notifier) ? notifier : [notifier]

  useSubscription(NotificationsSubscriptionDocument, {
    onData: useCallback(
      ({
        data,
      }: {
        data: {
          data?: {
            notificationsInboxNotificationCreate?: {
              inboxNotification?: Notification | null
            } | null
          }
        }
      }) => {
        const inboxNotification = data.data?.notificationsInboxNotificationCreate?.inboxNotification
        if (inboxNotification?.notifier && notifiers.includes(inboxNotification.notifier)) {
          callback(inboxNotification)
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [notifiers.join(','), callback]
    ),
  })
}
